import axios from 'axios';
import history from '../history';
import { getMsUntilExpiry, isJwt } from '../utils/jwt';

// Configuração da URL base da API usando variável de ambiente
//const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://192.168.68.113:9013';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://reactpainel.lukos.com.br/rpainel.dll/';

export const Servidor = API_BASE_URL;

const TOKEN_KEY = 'lukos-token-fw';
const REFRESH_ENDPOINT = '/faturaweb/validatokenPost';
// Endpoints de autenticação: um 401 aqui é resposta de NEGÓCIO (credencial
// inválida / e-mail não encontrado), não sessão expirada. Não devem disparar
// refresh nem logout — a própria tela trata o erro e mostra a mensagem certa.
const AUTH_ENDPOINTS = ['/faturaweb/login', '/faturaweb/RecuperarSenha'];
// Margem antes do `exp` para renovar proativamente (só se o token for JWT).
const PROACTIVE_MARGIN_MS = 60 * 1000;
// Piso do intervalo proativo: evita um ciclo de refresh muito curto caso um
// futuro JWT venha com vida menor que a margem acima.
const MIN_PROACTIVE_DELAY_MS = 15 * 1000;

// ---------------------------------------------------------------------------
// Token store (fino, centralizado). O resto do app ainda lê a chave direto do
// localStorage em vários lugares; manter a MESMA chave garante compatibilidade.
// ---------------------------------------------------------------------------
const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem('acesso');
};

// Cliente axios da aplicação (com interceptors).
const auth = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 segundos
});

// Cliente SEM interceptors, usado só para o refresh. Isso é o que evita
// recursão: um 401 no próprio refresh NÃO dispara outro refresh.
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// ---------------------------------------------------------------------------
// Logout centralizado — limpa a sessão e volta ao login sem empilhar navegações.
// ---------------------------------------------------------------------------
function forceLogout() {
  clearSession();
  cancelProactiveRefresh();
  if (!window.location.pathname.includes('/auth/login-page')) {
    history.push('/auth/login-page');
  }
}

// ---------------------------------------------------------------------------
// Refresh single-flight: se várias requisições tomarem 401 ao mesmo tempo, só
// UMA chamada de validatokenPost acontece; todas aguardam a mesma promise.
// ---------------------------------------------------------------------------
let refreshPromise = null;

function refreshToken() {
  if (refreshPromise) return refreshPromise;

  const chave = getToken();
  if (!chave) {
    // Sem token não há o que renovar.
    return Promise.reject(new Error('sem token para renovar'));
  }

  refreshPromise = refreshClient
    .post(REFRESH_ENDPOINT, { params: { chave } })
    .then((res) => {
      const novo = res?.data?.token;
      if (res.status === 200 && novo) {
        setToken(novo);
        scheduleProactiveRefresh(); // reprograma com base no novo token
        return novo;
      }
      throw new Error('refresh não retornou token');
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// Atualiza o token na requisição a ser repetida: no header (canal universal,
// injetado pelo interceptor) e, se existir, também no corpo `params.chave`
// (vários endpoints da Fatura Web mandam o token no body). Best-effort: se o
// body não tiver esse formato, segue só com o header.
function applyTokenToRequest(config, novoToken) {
  config.headers = config.headers || {};
  config.headers.token = novoToken;

  if (config.data == null) return config;
  try {
    if (typeof config.data === 'string') {
      const parsed = JSON.parse(config.data);
      if (parsed?.params && 'chave' in parsed.params) {
        parsed.params.chave = novoToken;
        config.data = JSON.stringify(parsed);
      }
    } else if (typeof config.data === 'object' && config.data.params && 'chave' in config.data.params) {
      config.data.params.chave = novoToken;
    }
  } catch {
    /* body não é JSON com params.chave — header já cobre o caso comum */
  }
  return config;
}

// ---------------------------------------------------------------------------
// Refresh proativo: só faz sentido se o token for JWT (temos `exp`). Para token
// opaco (caso atual provável), fica desativado e o app depende do refresh
// reativo no 401 — que já elimina os logouts inesperados.
// ---------------------------------------------------------------------------
let proactiveTimer = null;
let avisouTokenOpaco = false;

function cancelProactiveRefresh() {
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
}

export function scheduleProactiveRefresh() {
  cancelProactiveRefresh();
  const token = getToken();
  if (!token) return;

  const msUntilExpiry = getMsUntilExpiry(token);
  if (msUntilExpiry === null) {
    // Token sem `exp` (não-JWT): nada a agendar.
    if (!avisouTokenOpaco) {
      avisouTokenOpaco = true;
      // eslint-disable-next-line no-console
      console.info(
        '[auth] token não é JWT (sem exp): refresh proativo desativado; usando refresh reativo no 401.'
      );
    }
    return;
  }

  const delay = Math.max(msUntilExpiry - PROACTIVE_MARGIN_MS, MIN_PROACTIVE_DELAY_MS);
  proactiveTimer = setTimeout(() => {
    refreshToken().catch(() => forceLogout());
  }, delay);
}

// ---------------------------------------------------------------------------
// Interceptors
// ---------------------------------------------------------------------------

// Requisição: injeta o token no header se ainda não estiver presente.
auth.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token && !config.headers.token) {
      config.headers.token = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Resposta: em 401, tenta renovar a sessão e repetir a requisição uma vez.
auth.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;
    const url = originalRequest?.url || '';

    if (status === 401) {
      // 401 num endpoint de autenticação (login/recuperar senha) = credencial
      // recusada. Não renova nem desloga: devolve o erro ORIGINAL (com
      // error.response) para a tela mostrar "usuário ou senha inválida".
      const isAuthEndpoint = AUTH_ENDPOINTS.some((e) => url.includes(e));
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      // 401 no próprio refresh, sem config, ou requisição já repetida uma vez:
      // a sessão é realmente inválida → desloga (anti-loop).
      const isRefreshCall = url.includes(REFRESH_ENDPOINT);
      if (isRefreshCall || !originalRequest || originalRequest._retry) {
        forceLogout();
        return Promise.reject(error);
      }

      // Caso normal: renova a sessão e repete a requisição original uma vez.
      originalRequest._retry = true;
      try {
        const novoToken = await refreshToken();
        applyTokenToRequest(originalRequest, novoToken);
        return auth(originalRequest);
      } catch (refreshErr) {
        forceLogout();
        return Promise.reject(refreshErr);
      }
    }

    if (status === 403) {
      console.error('Acesso negado:', error.response?.data);
    }
    if (status === 500) {
      console.error('Erro interno do servidor:', error.response?.data);
    }

    return Promise.reject(error);
  }
);

// Ao carregar o módulo, se já houver sessão, arma o refresh proativo (no-op se
// o token for opaco). `isJwt` só serve para log de diagnóstico.
if (getToken()) {
  // eslint-disable-next-line no-console
  console.debug('[auth] sessão existente detectada; token JWT?', isJwt(getToken()));
  scheduleProactiveRefresh();
}

export default auth;
